import React, { useState, useContext } from "react";
import Logger from "../../util/logger";
import Call from "../../util/call";
import { Config, useSession } from "./session";
import { useAuth } from "./auth";
import { BankInfo, UserInfo } from "../../../../../types";

interface FormContextValue {
  invite: string,
  userType: string | null,
  email: string | null,
  error: boolean | number,
  checkInvite(invite: string): Promise<boolean>,
  checkRefund?: string,
  userInfo: any,
  bankInfo: any,
  setForeignBankInfo(
    bankName: string,
    bankCountry: string,
    bankAddress: string,
    bankCity: string,
    bankRegion: string,
    bankPostalCode?: string,
    accountCurrency?: string,
    sortCode?: string,
    swiftOrBicCode?: string,
    accntOrIBAN?: string,
    accountHolder?: string,
    paymentInstruct?: string): void
  updateUserInfo(userinfo: UserInfo, usertype: string, contactInfo?: boolean): void,
  newAccount: string,
  setNewAccount(isTrue: string): void,
  setCanadianBankInfo(branchNo: string, institutionNo: string, accountNo: string, accountHolder?: string): void,
  modify: string,
  setModify(isTrue: string): void,
  entity: string,
  setEntity(enity: string): void
  submitForm(): Promise<boolean>,
  timeSubmitted: number,
  contactInfo: boolean | null,
  resetBankInfo(): void,
  resetAllInfo(): void
}

const logger = Logger("form-provider");
export const FormContext = React.createContext({} as FormContextValue);

/**
 * Profile context
 */
export const FormProvider: React.FC = ({ children }) => {
  const [invite, setInvite] = useState<string>("");
  const [userType, setUserType] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<boolean | number>(false);

  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [contactInfo, setContactInfo] = useState<boolean | null>(null)
  const [newAccount, setNewAccount] = useState<string>("");
  const [bankInfo, setBankInfo] = useState<BankInfo>();
  const [modify, setModify] = useState<string>("");
  const [timeSubmitted, setTimeSubmitted] = useState(0);
  const [entity, setEntity] = useState<string>("")
  const { config } = useSession() as { config: Config };
  const { userDetails, inviteStatus, setInviteStatus } = useAuth();
  const [checkRefund, setcheckRefund] = useState<string>("x");
  const checkInvite = (urlInvite: string) => {
    return Call(config.API_URL, "public", "check-invite", { urlInvite })
      .then(({ response, status }) => {
        const { success, limit } = response;

        if (status !== 200) {
          logger.error(status);
          setError(status as number);
          setInviteStatus("error");
          return false;
        } else if (success) {
          setcheckRefund(response.checkRefund);
          setInvite(urlInvite);
          inviteStatus !== "limit" && setInviteStatus("valid");
          setUserType(response.userType);
          if (userType === "RAP") {
            setEmail(response.email);
          }
          return true;
        } else if (!success && limit) {
          setInviteStatus("limit");
          return false;
        }
        setInviteStatus("invalid");
        return false;
      })
  }

  const updateUserInfo = (userInfo: UserInfo, userType: string, contactInfo?: boolean): void => {
    switch (userType) {
      case "RAP":
        setContactInfo(contactInfo as boolean)
        setUserInfo(userInfo)
        break
      case "Employee":
        setUserInfo(userInfo)
        break
      case "BC":
        setUserInfo(userInfo)
        break
      case "Refund":
        setUserInfo(userInfo)
        break
      case "PAD":
        setUserInfo(userInfo)
        break
  
    }
  }

  const resetBankInfo = () => {
    setBankInfo(undefined);
  }

  const resetAllInfo = () =>{
    resetBankInfo();
    setUserInfo({});
    setModify("");
    setNewAccount("");
    setContactInfo(null);
    setEntity("");
  }
  const setCanadianBankInfo = (branchNo: string, institutionNo: string, accountNo: string, accountHolder?: "") => {
    setBankInfo({ isForeign: false, branchNo, institutionNo, accountNo, accountHolder });
  }

  const setForeignBankInfo = (
    bankName: string,
    bankCountry: string,
    bankAddress: string,
    bankCity: string,
    bankRegion: string,
    bankPostalCode?: "0000",
    accountCurrency?: string,
    sortCode?: "",
    swiftOrBicCode?: "",
    accntOrIBAN?: "",
    accountHolder?: "",
    paymentInstruct?: "") => {
    setBankInfo({
      isForeign: true,
      bankName,
      bankCountry,
      bankAddress,
      bankCity,
      bankRegion,
      bankPostalCode,
      accountCurrency,
      sortCode,
      swiftOrBicCode,
      accntOrIBAN,
      accountHolder,
      paymentInstruct
    });
  }

  const submitForm = (): Promise<boolean> => {
    const body = {
      invite: userDetails?.invite,
      userType,
      userId: userDetails?.userID,
      email: userDetails?.email,
      dob: userDetails?.dob,
      userInfo,
      bankInfo
    }
    return Call(config.API_URL, "public", "submit-form", body)
      .then(({ response, status }) => {
        const { success } = response;
        if (status !== 200) {
          logger.error(status);
          setError(status as number);
          return false;
        } else if (success) {
          setTimeSubmitted(response.timestamp)
          return true;
        }
        logger.error("An error occurred submitting the form");
        return false;
      })
  }

  return (
    <FormContext.Provider
      value={{
        invite,
        userType,
        email,
        checkInvite,
        checkRefund,
        error,
        userInfo,
        updateUserInfo,
        newAccount,
        setNewAccount,
        bankInfo,
        setCanadianBankInfo,
        setForeignBankInfo,
        modify,
        setModify,
        entity,
        setEntity,
        submitForm,
        timeSubmitted,
        contactInfo,
        resetBankInfo,
        resetAllInfo
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useForm = () => useContext(FormContext);
